import axios, { AxiosResponse } from "axios";
import {User} from "@/models/user_schema";
import { ActivitySchema } from "@/models/activity_schema";
import { AuthError, APIError } from "@/services/error_service";
/**
 * Creates a new Rep Service

 * @example
 * const repService = new RepService();
 * repService.getReps();
 */
 
axios.defaults.headers!.apikey = process.env.VUE_APP_API_KEY;
axios.defaults.withCredentials = true;

export default class RepService {
  /**
   * @returns {Rep[]} - response array of rep data objects
   */
  public async getReps(): Promise<User[]> {
    let reps;
    try {
      const options = {
        withCredentials: true,
      };
      const res: AxiosResponse<any> = await axios.get(
        process.env.VUE_APP_API_URL + "/reps",
        options
      );
      reps = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return reps;
  }

  /**
   * POST new rep
   * @param {Rep} request - new rep data object
   * @returns {Rep} - newly created rep object
   */
  public async createNewRep(request: User): Promise<User> {
    let rep;
    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    const body = JSON.stringify(request);
    try {
      const res: AxiosResponse<any> = await axios.post(
        process.env.VUE_APP_API_URL + "/reps",
        body,
        options
      );
      rep = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        } else if (err.response.status === 400) {
          throw new APIError("Invalid request. Please try again.");
        } else if (err.response.status === 409) {
          throw new APIError("A user with that email address already exists.");
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return rep;
  }

  /**
   * Get selected single rep
   * @param {number} id - rep id
   * @returns {Rep} - selected rep data object
   */
  public async getSingleRep(id: string): Promise<User> {
    let rep;
    const options = {
      withCredentials: true,
    };
    try {
      const res: AxiosResponse<any> = await axios.get(
        process.env.VUE_APP_API_URL + "/reps/" + id,
        options
      );
      rep = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return rep;
  }

  /**
   * Update current rep
   * @param {number} id - rep id
   * @param {Rep} request - rep request data
   * @returns {Promise<void>}
   */
  public async updateRep(request: User, _id: string): Promise<User> {
    let rep;
    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };
    const body = JSON.stringify(request);
    try {
      const res: AxiosResponse<any> = await axios.put(
        process.env.VUE_APP_API_URL + "/profile/" + _id,
        body,
        options
      );
      rep = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        } else if (err.response.status === 400) {
          throw new APIError("Invalid request. Please try again.");
        } else if (err.response.status === 409) {
          throw new APIError("A user with that email address already exists.");
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return rep;
  }

  /**
   * Delete rep
   * @param {number} id - rep id
   * @returns {Promise<void>}
   */
  public async deleteRep(id: string): Promise<void> {
    try {
      const options = {
        withCredentials: true,
      };
      return await axios.delete(
        process.env.VUE_APP_API_URL + "/users/" + id,
        options
      );
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
  }

  /**
   * Return repactivity notes
   * @param {number} id - rep id
   * @returns {Promise<ActivitySchema>} - array of activity notes
   */
  public async getRepActivities(id: string): Promise<ActivitySchema[]> {
    let activities;
    const options = {
      withCredentials: true,
    };
    try {
      const res: AxiosResponse<any> = await axios.get(
        process.env.VUE_APP_API_URL + "/reps/" + id + "/activities",
        options
      );
      activities = res.data.data;
    } catch (err) {
      if (err.response.status && err.response.status != 500) {
        if (err.response.status === 401) {
          throw new AuthError(err.response);
        } else if (err.response.status === 404) {
          throw new APIError("The requested information was not found.");
        } else if (err.response.status === 403) {
          throw new APIError(
            "You do not have access to the requested resource."
          );
        }
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    }
    return activities;
  }
}
